/**
 * Презентационные (демо) IFC-файлы, доступные в меню «+» → «Презентационные
 * файлы» — позволяют быстро открыть готовую модель без загрузки своего
 * файла (например, чтобы показать инструмент клиенту).
 *
 * Сами .ifc-файлы лежат в public/samples/ (см. public/samples/README.md —
 * они не хранятся в git из-за размера). PNG-миниатюры версионируются и
 * генерируются скриптом scripts/generate-presentation-thumbnails.mjs
 * (`npm run generate:thumbnails`).
 */
import { dom } from "../dom";
import { state } from "../state";
import { hideProgress, setProgress } from "../ui/progress";
import { setStatus } from "../ui/status";
import { escapeHtmlAttr, escapeHtmlText } from "../utils/html";
import { addOrReplaceIfcTab } from "./tabs";
import { closeIfcAddMenu } from "./addMenu";
import { getTheme } from "../ui/theme";

export interface PresentationFile {
  id: string;
  title: string;
  /** Путь относительно BASE_URL (без ведущего слэша), напр. "samples/foo.ifc". */
  fileName: string;
  thumbnailDark: string;
  thumbnailLight: string;
  sizeLabel: string;
}

export const PRESENTATION_FILES: PresentationFile[] = [
  {
    id: "medvedki-261-pp",
    title: "Отопление (полипропилен)",
    fileName: "samples/medvedki-261-polypropylene.ifc",
    thumbnailDark: "samples/medvedki-261-polypropylene-dark.png",
    thumbnailLight: "samples/medvedki-261-polypropylene-light.png",
    sizeLabel: "≈19 МБ",
  },
  {
    id: "medvedki-261",
    title: "Отопление (сшитый полиэтилен)",
    fileName: "samples/medvedki-261.ifc",
    thumbnailDark: "samples/medvedki-261-dark.png",
    thumbnailLight: "samples/medvedki-261-light.png",
    sizeLabel: "≈31 МБ",
  },
  {
    id: "zagorodny-dom",
    title: "Загородный дом",
    fileName: "samples/zagorodny-dom.ifc",
    thumbnailDark: "samples/zagorodny-dom-dark.png",
    thumbnailLight: "samples/zagorodny-dom-light.png",
    sizeLabel: "≈18 МБ",
  },
];

export function presentationThumbnailPath(pf: PresentationFile): string {
  const rel = getTheme() === "light" ? pf.thumbnailLight : pf.thumbnailDark;
  return `${assetUrl(rel)}?v=${PRESENTATION_CACHE_REV}`;
}

function assetUrl(relPath: string): string {
  return `${import.meta.env.BASE_URL}${relPath}`;
}

/**
 * Сбрасывает закэшированный 404: файлы появились на сервере после первого
 * деплоя, а `force-cache` иначе продолжает отдавать старый промах.
 */
const PRESENTATION_CACHE_REV = "5";

function presentationIdFromPath(): string | null {
  const match = window.location.pathname.match(/\/p\/([a-z0-9-]+)\/?$/i);
  return match?.[1] ?? null;
}

export function currentPresentationId(): string | null {
  const fromQuery = new URLSearchParams(window.location.search).get("presentation");
  return fromQuery || presentationIdFromPath();
}

function presentationOpenUrl(id: string): string {
  const origin = import.meta.env.VITE_ANALYTICS_MODE === "public"
    ? window.location.origin
    : "https://viewer.ingproff.ru";
  return `${origin}/p/${id}`;
}

const COPY_ICON =
  `<svg viewBox="0 0 16 16" aria-hidden="true" width="14" height="14">` +
  `<rect x="5" y="5" width="8" height="8" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.4"/>` +
  `<path d="M4 11H3.5A1.5 1.5 0 0 1 2 9.5v-6A1.5 1.5 0 0 1 3.5 2h6A1.5 1.5 0 0 1 11 3.5V4" fill="none" stroke="currentColor" stroke-width="1.4"/>` +
  `</svg>`;

export function renderPresentationList(): void {
  if (!dom.ifcPresentationList) {
    return;
  }
  dom.ifcPresentationList.innerHTML = PRESENTATION_FILES.map((pf) => {
    const thumbUrl = escapeHtmlAttr(presentationThumbnailPath(pf));
    const title = escapeHtmlText(pf.title);
    const titleAttr = escapeHtmlAttr(pf.title);
    const meta = escapeHtmlText(pf.sizeLabel);
    const id = escapeHtmlAttr(pf.id);
    return (
      `<div class="ifc-presentation-card" role="button" tabindex="0" data-presentation-id="${id}" title="${titleAttr}">` +
      `<img class="ifc-presentation-thumb" src="${thumbUrl}" alt="" loading="lazy" />` +
      `<span class="ifc-presentation-title">${title}</span>` +
      `<span class="ifc-presentation-meta-row">` +
      `<span class="ifc-presentation-meta">${meta}</span>` +
      `<button type="button" class="ifc-presentation-copy" data-presentation-copy="${id}" title="Скопировать ссылку" aria-label="Скопировать ссылку на «${titleAttr}»">${COPY_ICON}</button>` +
      `</span>` +
      `</div>`
    );
  }).join("");
}

async function fetchPresentationAsFile(pf: PresentationFile): Promise<File> {
  const url = `${assetUrl(pf.fileName)}?v=${PRESENTATION_CACHE_REV}`;
  const response = await fetch(url, { cache: "force-cache" });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  const buf = await response.arrayBuffer();
  const name = pf.fileName.split("/").pop() || `${pf.id}.ifc`;
  return new File([buf], name, { type: "model/ifc" });
}

export async function openPresentationFile(id: string): Promise<void> {
  const pf = PRESENTATION_FILES.find((p) => p.id === id);
  if (!pf) {
    return;
  }
  const card = dom.ifcPresentationList.querySelector<HTMLElement>(
    `[data-presentation-id="${CSS.escape(id)}"]`,
  );
  card?.classList.add("is-loading");
  const loadingText = `Загрузка презентационного файла: ${pf.title}…`;
  setStatus(loadingText);
  // Строка статуса лежит внутри боковой панели и на телефоне её не видно,
  // пока панель свёрнута. Спиннер — на весь экран.
  setProgress(null, loadingText);
  try {
    const file = await fetchPresentationAsFile(pf);
    closeIfcAddMenu();
    state.pendingOpenMode = "new-tab";
    state.pendingModelSource = "presentation";
    state.pendingPresentationId = pf.id;
    await addOrReplaceIfcTab(file, "new-tab");
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    const errorText = `Не удалось загрузить презентационный файл «${pf.title}»: ${message}`;
    setStatus(errorText, true);
    setProgress(null, errorText);
    window.setTimeout(() => hideProgress(), 6000);
  } finally {
    card?.classList.remove("is-loading");
  }
}

export function initPresentationMenu(): void {
  renderPresentationList();
  window.addEventListener("ifc-viewer-theme", () => {
    renderPresentationList();
  });
  dom.ifcPresentationList.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    const copyBtn = target.closest<HTMLElement>("[data-presentation-copy]");
    if (copyBtn) {
      event.preventDefault();
      event.stopPropagation();
      const id = copyBtn.dataset.presentationCopy;
      if (id) {
        void copyPresentationLink(id, copyBtn);
      }
      return;
    }
    const card = target.closest<HTMLElement>("[data-presentation-id]");
    if (!card) {
      return;
    }
    const id = card.dataset.presentationId;
    if (!id) {
      return;
    }
    void openPresentationFile(id);
  });
  dom.ifcPresentationList.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }
    const target = event.target;
    if (!(target instanceof HTMLElement) || target.closest("[data-presentation-copy]")) {
      return;
    }
    const card = target.closest<HTMLElement>("[data-presentation-id]");
    if (!card || event.target !== card) {
      return;
    }
    event.preventDefault();
    const id = card.dataset.presentationId;
    if (id) {
      void openPresentationFile(id);
    }
  });
  const requested = currentPresentationId();
  if (requested && !new URLSearchParams(window.location.search).get("fileId") && !new URLSearchParams(window.location.search).get("src") && PRESENTATION_FILES.some((pf) => pf.id === requested)) {
    void openPresentationFile(requested);
  }
}

export async function copyPresentationLink(id: string, button: HTMLElement): Promise<void> {
  const url = presentationOpenUrl(id);
  try {
    await writeClipboard(url);
    setStatus("Ссылка скопирована");
    button.classList.add("is-copied");
    const previous = button.getAttribute("title");
    button.setAttribute("title", "Ссылка скопирована");
    window.setTimeout(() => {
      button.classList.remove("is-copied");
      if (previous) {
        button.setAttribute("title", previous);
      }
    }, 1200);
  } catch {
    setStatus("Не удалось скопировать ссылку", true);
  }
}

async function writeClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Жест клика по вложенной иконке браузер иногда не считает разрешением.
    }
  }
  const area = document.createElement("textarea");
  area.value = text;
  area.setAttribute("readonly", "");
  area.style.position = "fixed";
  area.style.top = "0";
  area.style.left = "0";
  area.style.opacity = "0";
  document.body.appendChild(area);
  area.focus();
  area.select();
  const copied = document.execCommand("copy");
  area.remove();
  if (!copied) {
    throw new Error("copy failed");
  }
}
